/**
 * Custom voices: clone a voice from sample media (an upload here, or a clip in a project from the
 * voice dialog). Everyone sees every voice; the person who made one, or an admin, can delete it.
 */

import { ArrowLeft, Loader2, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, type CloneStatus as Status, type Job, type VoiceInfo } from "./api";
import { appUrl } from "./base";

export const voiceSampleUrl = (id: string) => appUrl(`/api/voices/${id}/sample`);

function seconds(t: number) {
  return `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}`;
}

export async function waitForJob(start: Promise<Job>, onProgress: (j: Job) => void): Promise<Job> {
  let job = await start;
  while (["queued", "running"].includes(job.status)) {
    onProgress(job);
    await new Promise((r) => setTimeout(r, 700));
    job = await api.job(job.id);
  }
  if (job.status !== "done") throw new Error(job.message || "failed");
  return job;
}

export function CloneStatus() {
  const [st, setSt] = useState<Status | null>(null);
  useEffect(() => {
    api.cloneStatus().then(setSt).catch((e) => setSt({ ok: false, error: e.message }));
  }, []);
  if (!st) return null;
  if (st.ok) return <span className="good small">Voice cloning is on ({st.device === "cuda" ? "graphics card" : "CPU, slow"}).</span>;
  return <span className="bad small">{st.error ?? "The voice cloning service is off."} Start it on the server with <code>docker compose --profile clone up -d</code>.</span>;
}

export function VoicesPage({ admin, me }: { admin: boolean; me: string }) {
  const [list, setList] = useState<VoiceInfo[] | null>(null);
  const [name, setName] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState<{ what: string; job?: Job } | null>(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const refresh = () => api.voices().then((v) => setList(v.filter((x) => x.custom))).catch((e) => setErr(e.message));
  useEffect(() => void refresh(), []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = file.current?.files?.[0];
    if (!f) return setErr("Choose an audio or video file first.");
    setErr("");
    setMsg("");
    setBusy({ what: "Uploading" });
    try {
      const start = range.start ? Number(range.start) : undefined;
      const end = range.end ? Number(range.end) : undefined;
      const j = await waitForJob(api.createVoice(f, name, start, end), (job) => setBusy({ what: "Making the voice", job }));
      setMsg(j.output?.warning ?? `“${j.output?.voice?.name}” is ready.`);
      setName("");
      setRange({ start: "", end: "" });
      if (file.current) file.current.value = "";
      await refresh();
    } catch (x) {
      setErr((x as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (v: VoiceInfo) => {
    if (!confirm(`Delete the voice “${v.name}”? Voiceovers already made with it keep playing.`)) return;
    try {
      await api.deleteVoice(v.id);
      await refresh();
    } catch (x) {
      setErr((x as Error).message);
    }
  };

  return (
    <div className="projects voices-page">
      <header>
        <h1>Voices</h1>
        <a className="button" href="#/"><ArrowLeft size={15} /> Projects</a>
      </header>
      <p className="muted small">
        A custom voice is cloned from a short sample: Studio keeps about ten seconds of clear speech from the file and the voice model imitates it
        when it speaks new words. Use one speaker, no music, no echo; whole sentences in an even tone give the best match. You can also make a voice
        from a voiceover already in a project (Change voice → Make a voice from this recording). <CloneStatus />
      </p>
      <form className="account-box vo-form" onSubmit={create}>
        <h3>New voice from sample media</h3>
        <div className="voice-controls">
          <input ref={file} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.mp4,.mov,.mkv,.webm" />
          <input placeholder="Name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
          <label title="Which part of the file to clone (seconds). Leave empty to let Studio pick the clearest whole sentences.">
            From <input className="num short" type="number" min={0} step={0.1} placeholder="auto" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
            to <input className="num short" type="number" min={0} step={0.1} placeholder="auto" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /> s
          </label>
          <span className="grow" />
          <button className="primary" disabled={!!busy}><Upload size={14} /> Make the voice</button>
        </div>
        {busy && (
          <p className="muted small"><Loader2 size={13} className="spin" /> {busy.job?.message && busy.job.message !== "starting" ? busy.job.message : busy.what}
            {busy.job?.status === "queued" ? " (waiting for the worker)" : busy.job ? ` ${Math.round(busy.job.progress * 100)}%` : "…"}</p>
        )}
        {err && <p className="bad small">{err}</p>}
        {msg && <p className="good small">{msg}</p>}
      </form>

      <h3 className="vo-heading">Custom voices</h3>
      {list === null ? <p className="muted">Loading…</p> : list.length === 0 ? <p className="muted">None yet.</p> : (
        <div className="vo-list">
          {list.map((v) => (
            <div className="vo-row" key={v.id}>
              <div className="vo-meta">
                <strong>{v.name}</strong>
                <span className="muted small">by {v.byName} · {new Date((v.created ?? 0) * 1000).toLocaleDateString()} · from {v.source || "an upload"}, {seconds(v.seconds ?? 0)} kept</span>
                {v.text && <span className="muted small vo-said" title="What the reference clip says">“{v.text}”</span>}
              </div>
              {v.sample ? <audio controls preload="none" src={voiceSampleUrl(v.id)} title="A sample sentence spoken with this voice" /> : <span className="muted small">No sample (the cloning service was off when the voice was made).</span>}
              <div className="vo-actions">
                <a className="button" href={appUrl(`/api/voices/${v.id}/reference`)} target="_blank" rel="noreferrer" title="Hear the reference clip the voice is cloned from">Reference</a>
                {(admin || v.by === me) && <button className="danger" title="Delete this voice" onClick={() => void remove(v)}><Trash2 size={13} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
